import { type FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError } from '../../utils/api-error';
import { apiPublic } from '../../utils/data-utils';

type LinkStatus = 'pending' | 'expired' | 'submitted';
const maxReasonLength = 2000;

function Declaration({ tenant, declarationId }: { tenant: string; declarationId: string }) {
  const [status, setStatus] = useState<LinkStatus>();
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const charactersRemaining = maxReasonLength - reason.length;
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const message = useRef<HTMLDivElement>(null);
  const path = `/out-of-scope/${encodeURIComponent(tenant)}/${encodeURIComponent(declarationId)}`;

  useEffect(() => {
    let cancelled = false;
    apiPublic(path, { cache: 'no-store' })
      .then((response) => response.json())
      .then((body) => {
        if (!cancelled) setStatus(body.status);
      })
      .catch((cause) => {
        if (!cancelled)
          setError(
            cause instanceof ApiError
              ? cause.detail || cause.message
              : 'Unable to load your link. Please try again.',
          );
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  useEffect(() => {
    if (error || status === 'submitted') message.current?.focus();
  }, [error, status]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) return;
    if (!reason.trim()) {
      setError('Please briefly advise why you are out of scope.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const response = await apiPublic(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: reason.trim(),
          ...(status === 'expired' ? { email: email.trim() } : {}),
        }),
      });
      const body = await response.json();
      setStatus(body.status);
    } catch (cause) {
      setError(
        cause instanceof ApiError
          ? cause.detail || cause.message
          : 'Unable to save your declaration. Please try again.',
      );
      try {
        const response = await apiPublic(path, { cache: 'no-store' });
        const body = await response.json();
        setStatus(body.status);
        if (body.status === 'submitted') setError('');
      } catch {
        // preserve submission error when status refresh also fails
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="govuk-width-container govuk-main-wrapper">
      <div className="govuk-grid-row">
        <div
          className="govuk-grid-column-two-thirds"
          style={status === 'submitted' ? { float: 'none', marginInline: 'auto' } : undefined}
        >
          <h1 className="govuk-heading-l">Out of scope declaration</h1>
          {error && (
            <div className="govuk-error-summary" role="alert" tabIndex={-1} ref={message}>
              {error}
            </div>
          )}
          {status === 'submitted' ? (
            <div
              className="govuk-panel govuk-panel--confirmation"
              tabIndex={-1}
              ref={message}
              role="status"
            >
              <h2 className="govuk-panel__title govuk-!-font-size-36">Declaration submitted</h2>
              <div className="govuk-panel__body govuk-!-font-size-27">
                Thank you. Your out of scope declaration has been recorded. You can close this page.
              </div>
            </div>
          ) : status ? (
            <form onSubmit={submit}>
              {status === 'expired' && (
                <div className="govuk-form-group">
                  <p className="govuk-body">
                    Your original link details have expired. Please enter your email address with
                    your declaration.
                  </p>
                  <label className="govuk-label" htmlFor="declaration-email">
                    Email address
                  </label>
                  <input
                    className="govuk-input"
                    id="declaration-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    maxLength={254}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                </div>
              )}
              <div className="govuk-form-group">
                <label className="govuk-label" htmlFor="declaration-reason">
                  Please briefly advise why you are out of scope.
                </label>
                <textarea
                  className="govuk-textarea"
                  id="declaration-reason"
                  name="reason"
                  rows={6}
                  required
                  maxLength={maxReasonLength}
                  aria-describedby="declaration-reason-count"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
                <p
                  className="govuk-hint"
                  id="declaration-reason-count"
                  style={{ marginTop: 0 }}
                  aria-live="polite"
                  aria-atomic="true"
                >
                  You have {charactersRemaining} character{charactersRemaining === 1 ? '' : 's'}
                  {' remaining'}
                </p>
              </div>
              <button className="govuk-button" type="submit" disabled={saving}>
                {saving ? 'Submitting...' : 'Submit'}
              </button>
            </form>
          ) : (
            !error && (
              <p className="govuk-body" role="status">
                Loading...
              </p>
            )
          )}
        </div>
      </div>
    </main>
  );
}

export default function OutOfScope() {
  const { tenant = '', declarationId = '' } = useParams();
  return (
    <Declaration key={`${tenant}/${declarationId}`} tenant={tenant} declarationId={declarationId} />
  );
}
